(() => {
  if (window.__productUpsellAddonsBound) return;
  window.__productUpsellAddonsBound = true;

  const ROOT_SELECTOR = '[data-upsell-root]';
  const CARD_SELECTOR = '[data-upsell-select]';
  const INFO_SELECTOR = '[data-modal-trigger]';
  const NAV_SELECTOR = '[data-upsell-nav]';
  const MODAL_ADD_SELECTOR = '.upsell-modal-btn[data-variant-id]';

  const selectedUpsells = new Set();
  let modal = null;
  let lastTrigger = null;
  let activeRoot = null;
  let isSubmitting = false;

  function isCartPage() {
    return document.body.classList.contains('template-cart');
  }

  function getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCartAddUrl() {
    const base = (window.routes && window.routes.cart_add_url) || `${getShopifyRoot()}cart/add`;
    return `${String(base).replace(/\.js$/, '')}.js`;
  }

  function getCartUrl() {
    return (window.routes && window.routes.cart_url) || `${getShopifyRoot()}cart`;
  }

  function getCartUi() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getUpsellRoots() {
    return Array.from(document.querySelectorAll(ROOT_SELECTOR));
  }

  function isRootVisible(root) {
    return !!(root && (root.offsetWidth || root.offsetHeight || root.getClientRects().length));
  }

  function getVisibleUpsellRoot() {
    return getUpsellRoots().find((root) => isRootVisible(root)) || getUpsellRoots()[0] || null;
  }

  function resolveUpsellRoot(source) {
    return source?.closest?.(ROOT_SELECTOR) || activeRoot || getVisibleUpsellRoot();
  }

  function setStatus(root, message, type) {
    const status = root?.querySelector('[data-upsell-status]');
    if (!status) return;
    status.textContent = message || '';
    status.dataset.type = type || '';
    status.hidden = !message;
  }

  function setButtonState(button, loading, defaultLabel) {
    if (!button) return;
    button.disabled = !!loading;
    button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    button.textContent = loading ? 'Adding...' : defaultLabel || 'Add to cart';
  }

  function initModal(root) {
    const nextRoot = root || activeRoot || getVisibleUpsellRoot();
    const nextModal = nextRoot?.querySelector('.upsell-modal-overlay') || document.querySelector('.upsell-modal-overlay');

    if (!nextModal) {
      modal = null;
      return null;
    }

    document.querySelectorAll('.upsell-modal-overlay[data-upsell-mounted="true"]').forEach((existingModal) => {
      if (existingModal !== nextModal) {
        existingModal.remove();
      }
    });

    if (nextModal.parentNode !== document.body) {
      document.body.appendChild(nextModal);
    }

    nextModal.dataset.upsellMounted = 'true';
    modal = nextModal;

    return modal;
  }

  function getCardFromSource(source) {
    return source?.closest?.(CARD_SELECTOR) || null;
  }

  function getCardData(card) {
    if (!card) return null;

    return {
      variantId: String(card.dataset.variantId || ''),
      variantPrice: parseInt(card.dataset.variantPrice || '0', 10) || 0,
      title: card.dataset.productTitle || '',
      price: card.dataset.productPrice || '',
      description: card.dataset.productDescription || '',
      image: card.dataset.productImage || '',
      imageAlt: card.dataset.productImageAlt || card.dataset.productTitle || '',
    };
  }

  function populateModal(card) {
    if (!modal || !card) return null;

    const data = getCardData(card);
    if (!data?.variantId) return null;

    const modalTitle = modal.querySelector('[data-modal-title]');
    const modalDescription = modal.querySelector('[data-modal-description]');
    const modalPrice = modal.querySelector('[data-modal-price]');
    const modalImage = modal.querySelector('[data-modal-image]');
    const modalButton = modal.querySelector('.upsell-modal-btn');

    if (modalTitle) modalTitle.textContent = data.title;
    if (modalDescription) modalDescription.textContent = data.description;
    if (modalPrice) modalPrice.textContent = data.price;

    if (modalImage) {
      if (data.image) {
        modalImage.src = data.image;
        modalImage.alt = data.imageAlt;
        modalImage.hidden = false;
      } else {
        modalImage.removeAttribute('src');
        modalImage.alt = '';
        modalImage.hidden = true;
      }
    }

    if (modalButton) {
      modalButton.dataset.variantId = data.variantId;
    }

    return data;
  }

  function getCardSelectorByVariantId(variantId) {
    return `${CARD_SELECTOR}[data-variant-id="${String(variantId || '')}"]`;
  }

  function getCartTotalValueElement() {
    return document.querySelector('[data-cart-total-value]');
  }

  function getMoneyFormat(root) {
    // Both roots' data-money-format come from the same shop-level Liquid
    // setting, so this is only unset if the DOM is missing entirely — in
    // which case formatMoneyValue below falls back to a plain number.
    return root?.dataset.moneyFormat || getCartTotalValueElement()?.dataset.moneyFormat || '';
  }

  function formatMoneyValue(cents, root) {
    const normalizedCents = Number(cents || 0);
    const format = getMoneyFormat(root);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(normalizedCents, format);
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const placeholder = format.match(placeholderRegex)?.[1];

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (Number.isNaN(number) || number == null) {
        return '0';
      }

      const amount = (number / 100).toFixed(precision);
      const parts = amount.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';

      return `${dollars}${centsPart}`;
    }

    let formattedValue = formatWithDelimiters(normalizedCents, 2);

    switch (placeholder) {
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(normalizedCents, 0);
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(normalizedCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(normalizedCents, 0, '.', ',');
        break;
      case 'amount':
      default:
        formattedValue = formatWithDelimiters(normalizedCents, 2);
        break;
    }

    return placeholder ? format.replace(placeholderRegex, formattedValue) : formattedValue;
  }

  function getBasePrice(root) {
    const basePrice = parseInt(root?.dataset.basePrice || '0', 10);
    return Number.isNaN(basePrice) ? 0 : basePrice;
  }

  function getSelectedPreviewPrice(root) {
    const selectedCard = root?.querySelector(`${CARD_SELECTOR}.is-selected`);
    const selectedPrice = parseInt(selectedCard?.dataset.variantPrice || '0', 10);
    return Number.isNaN(selectedPrice) ? 0 : selectedPrice;
  }

  function getPreviewTotal(root) {
    return getBasePrice(root) + getSelectedPreviewPrice(root);
  }

  function updatePreviewTotal(root) {
    if (!isCartPage()) return;

    // After a completed toggle the re-rendered data-base-price ALREADY includes
    // every selected add-on — re-adding the selected card's price double-counts
    // it (live bug 2026-07-28: sticky showed ₱3,794 vs actual ₱3,495, and kept
    // overquoting after removal). The canonical writer is updateCheckoutMeta()
    // in main-cart-footer.liquid — it reads [data-cart-total-value] fresh.
    // P8 2026-08-10: event contract na ('cart:meta-update'); sa cart page ang
    // FlowercartCartFooter module ang laging nakikinig, kaya sapat ang dispatch.
    document.dispatchEvent(new CustomEvent('cart:meta-update'));
  }

  function highlightCartPageCard(card) {
    // Cart page: clicking a card toggles it add/remove against the live cart.
    void toggleCartPageUpsell(card);
  }

  function getCartPageSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    return [
      mainCartItems
        ? {
            section: mainCartItems.dataset.id,
            selector: '#main-cart-items .js-contents',
          }
        : null,
      mainCartFooter
        ? {
            section: mainCartFooter.dataset.id,
            selector: '#main-cart-footer .js-contents',
          }
        : null,
      mainCartItems
        ? {
            section: mainCartItems.dataset.id,
            selector: '#main-cart-items .cart-upsell-slot--desktop',
          }
        : null,
      mainCartFooter
        ? {
            section: mainCartFooter.dataset.id,
            selector: '#main-cart-footer .cart-upsell-slot--mobile',
          }
        : null,
      {
        section: 'cart-icon-bubble',
        // The page renders the bubble inside a#cart-icon-bubble (header.liquid);
        // the section-render JSON wraps it in a .shopify-section div — swap the
        // anchor's children with the wrapper's children (same as Dawn cart.js).
        selector: '#cart-icon-bubble',
        sourceSelector: '.shopify-section',
      },
    ].filter(Boolean);
  }

  function replaceSectionFragment(sectionHtml, selector, sourceSelector) {
    if (!sectionHtml || !selector) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceElement = parsed.querySelector(sourceSelector || selector);
    const targetElement = document.querySelector(selector);

    if (sourceElement && targetElement) {
      // Children-only swap — never replace the node itself. The cart footer's
      // checkout-meta MutationObserver (main-cart-footer.liquid) is bound to the
      // `.js-contents` element; replaceWith() detaches it and permanently kills
      // the checkout-button total sync until reload (verified live 2026-07-28).
      targetElement.replaceChildren(...sourceElement.childNodes);
      return;
    }

    if (!sourceElement && targetElement) {
      targetElement.replaceChildren();
    }
  }

  function resetMountedModals() {
    document.querySelectorAll('.upsell-modal-overlay[data-upsell-mounted="true"]').forEach((existingModal) => {
      existingModal.remove();
    });

    modal = null;
  }

  async function refreshCartPageSections(variantId) {
    const sectionsToRender = getCartPageSectionsToRender();
    const sectionIds = [...new Set(sectionsToRender.map((entry) => entry.section).filter(Boolean))];

    if (!sectionIds.length) return;

    const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    const parsedSections = await response.json();

    sectionsToRender.forEach((entry) => {
      replaceSectionFragment(parsedSections[entry.section], entry.selector, entry.sourceSelector);
    });

    resetMountedModals();
    activeRoot = getVisibleUpsellRoot();
    initNavForAllRoots();

    const nextCard = activeRoot?.querySelector(getCardSelectorByVariantId(variantId));
    selectedUpsells.clear();

    syncSelectedCards();

    if (nextCard && typeof nextCard.focus === 'function') {
      nextCard.focus();
    } else if (activeRoot) {
      setStatus(activeRoot, 'Add-on added to order.', 'success');
    }
  }

  function syncSelectedCards() {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const variantId = String(card.dataset.variantId || '');
      const isSelected = selectedUpsells.has(variantId);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    updatePreviewTotal(activeRoot || getVisibleUpsellRoot());
  }

  function toggleUpsellCard(card) {
    if (!card) return;

    const variantId = String(card.dataset.variantId || '');
    if (!variantId) return;

    if (isCartPage()) {
      highlightCartPageCard(card);
      return;
    }

    if (selectedUpsells.has(variantId)) {
      selectedUpsells.delete(variantId);
      card.classList.remove('is-selected');
      card.setAttribute('aria-pressed', 'false');
    } else {
      selectedUpsells.add(variantId);
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');
    }
  }

  function selectUpsellByVariantId(variantId) {
    const id = String(variantId || '');
    if (!id) return;
    selectedUpsells.add(id);
    syncSelectedCards();
  }

  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.disabled && el.offsetParent !== null);
  }

  function trapModalTab(event) {
    if (!modal || !modal.classList.contains('is-active')) return;

    const focusable = getFocusableElements(modal);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openModal(card, triggerEl) {
    activeRoot = resolveUpsellRoot(triggerEl || card);
    modal = initModal(activeRoot);
    if (!modal) return;

    const data = populateModal(card);
    if (!data?.variantId) return;

    // On the cart page, selection state is owned by the live cart — opening
    // the info modal must not mark a card as selected. The modal's
    // "Add to order" button performs the actual toggle.

    lastTrigger = triggerEl || null;

    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const closeButton = modal.querySelector('[data-modal-close]');
    // Deferred: a real (trusted) click on the trigger button re-applies the
    // browser's own focus-follows-click after this handler returns, which
    // would otherwise clobber the focus-into-dialog step below.
    if (closeButton && typeof closeButton.focus === 'function') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => closeButton.focus());
      });
    }
  }

  function closeModal(restoreFocus = true) {
    if (!modal) return;
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (restoreFocus && lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  function getProductFormFromEventTarget(target) {
    if (!target) return null;

    return (
      target.closest('product-form form') ||
      target.closest('form[action*="/cart/add"]') ||
      target.closest('form[id^="product-form-"]')
    );
  }

  function buildItemsPayload(productForm) {
    const variantInput = productForm.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) {
      throw new Error('Main product variant not found.');
    }

    const qtyInput = productForm.querySelector('[name="quantity"]');
    const mainQty = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    const items = [
      {
        id: Number(variantInput.value),
        quantity: mainQty,
      },
    ];

    selectedUpsells.forEach((variantId) => {
      const numericId = Number(variantId);
      if (!numericId) return;

      items.push({
        id: numericId,
        quantity: 1,
        properties: {
          _upsell_source: 'Product Page Upsell',
        },
      });
    });

    return items;
  }

  async function refreshCartUi(addedData) {
    const cartUi = getCartUi();
    if (!cartUi) {
      window.location.href = getCartUrl();
      return;
    }

    try {
      const sections =
        typeof cartUi.getSectionsToRender === 'function'
          ? cartUi
              .getSectionsToRender()
              .map((section) => section.id)
              .filter(Boolean)
          : [];

      if (!sections.length) {
        window.location.href = getCartUrl();
        return;
      }

      const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const sectionData = await response.json();

      cartUi.renderContents({
        sections: sectionData,
        id: addedData?.items?.[0]?.id || addedData?.id,
      });

      if (cartUi.tagName === 'CART-DRAWER' && typeof cartUi.open === 'function') {
        cartUi.open();
      }
    } catch (error) {
      window.location.href = getCartUrl();
    }
  }

  async function addMainAndUpsells(productForm) {
    const root = activeRoot || getVisibleUpsellRoot();
    if (root) setStatus(root, '', '');

    const items = buildItemsPayload(productForm);

    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add items to cart.');
    }

    await refreshCartUi(data);
  }

  // --- Cart write engine (cart page) -------------------------------------
  // Serialize cart writes so rapid clicks never race. Uses the native
  // /cart/update.js endpoint (idempotent set-quantity) so the same card can
  // toggle add (qty 1) <-> remove (qty 0) reliably, with Section Rendering
  // built into the response (one round-trip).
  let cartWriteChain = Promise.resolve();
  const variantsInFlight = new Set();

  function enqueueCartWrite(task) {
    const run = cartWriteChain.then(task, task);
    // Keep the chain alive even if a task rejects.
    cartWriteChain = run.catch(() => {});
    return run;
  }

  function getCartUpdateUrl() {
    const base = (window.routes && window.routes.cart_update_url) || `${getShopifyRoot()}cart/update`;
    return `${String(base).replace(/\.js$/, '')}.js`;
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function postCartUpdate(updates, attempts = 3) {
    const sectionsToRender = getCartPageSectionsToRender();
    const sectionIds = [...new Set(sectionsToRender.map((entry) => entry.section).filter(Boolean))];

    let lastError;
    for (let i = 0; i < attempts; i += 1) {
      try {
        const response = await fetch(getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            updates,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });

        // Rate limited or transient server error: back off and retry,
        // honouring Retry-After when present. Never parse the body here —
        // Shopify returns an HTML error page (not JSON) for 429/5xx.
        if (response.status === 429 || response.status >= 500) {
          const retryAfterHeader = parseFloat(response.headers.get('Retry-After') || '');
          const backoffMs = Number.isFinite(retryAfterHeader)
            ? retryAfterHeader * 1000
            : 400 * Math.pow(2, i); // 400ms, 800ms, 1600ms
          lastError = new Error(
            response.status === 429
              ? 'Cart is busy. Please try again in a moment.'
              : 'Could not update cart. Please try again.'
          );
          if (i < attempts - 1) {
            await delay(backoffMs);
            continue;
          }
          throw lastError;
        }

        // Read as text first so a non-JSON body never throws a raw
        // "Unexpected token '<'" at the user.
        const raw = await response.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch (parseError) {
          throw new Error('Could not update cart. Please try again.');
        }

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not update cart.');
        }

        return data;
      } catch (error) {
        lastError = error;
        // Network/parse failures are worth one more try with a short pause;
        // logical errors above already decided whether to loop.
        if (i < attempts - 1) {
          await delay(400 * Math.pow(2, i));
        }
      }
    }

    throw lastError || new Error('Could not update cart.');
  }

  function setSubtotalShimmer(isOn) {
    document
      .querySelectorAll('[data-cart-total-value], [data-cart-sticky-total]')
      .forEach((el) => el.classList.toggle('is-updating', !!isOn));
  }

  function setCardLoading(card, state) {
    if (!card) return;
    if (state) {
      card.classList.add('is-loading');
      card.dataset.loadingState = state; // 'adding' | 'removing'
    } else {
      card.classList.remove('is-loading');
      delete card.dataset.loadingState;
    }
  }

  function applyOptimisticSelection(card, variantId, isSelected) {
    if (isSelected) {
      selectedUpsells.add(variantId);
    } else {
      selectedUpsells.delete(variantId);
    }
    if (card) {
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }
  }

  // Reconcile every card's selected-state against the live cart so the UI
  // never drifts from the server (covers reload + line-item removal in the
  // cart table). Selection is derived purely from /cart.js.
  async function syncWithLiveCart() {
    if (!isCartPage()) return;

    let liveIds;
    try {
      const res = await fetch(`${getCartUrl()}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      liveIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
    } catch (error) {
      return;
    }

    selectedUpsells.clear();
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const variantId = String(card.dataset.variantId || '');
      const isSelected = liveIds.has(variantId);
      if (isSelected) selectedUpsells.add(variantId);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    updatePreviewTotal(activeRoot || getVisibleUpsellRoot());
  }

  function renderCartSectionsFromUpdate(data, variantId) {
    const sectionsToRender = getCartPageSectionsToRender();
    const parsedSections = data && data.sections ? data.sections : {};

    sectionsToRender.forEach((entry) => {
      replaceSectionFragment(parsedSections[entry.section], entry.selector, entry.sourceSelector);
    });

    resetMountedModals();
    activeRoot = getVisibleUpsellRoot();
    initNavForAllRoots();
  }

  // Add (qty 1) or remove (qty 0) the same upsell card. The card itself is
  // the toggle — no separate remove control, matching the reference UX.
  async function toggleCartPageUpsell(card) {
    if (!card) return;

    activeRoot = resolveUpsellRoot(card);
    const variantId = String(card.dataset.variantId || '');
    if (!variantId) return;
    if (variantsInFlight.has(variantId)) return;

    const root = activeRoot || getVisibleUpsellRoot();
    if (root) setStatus(root, '', '');

    const wasSelected = card.classList.contains('is-selected');
    const willSelect = !wasSelected;

    // Optimistic UI: flip the card immediately, then confirm over the wire.
    applyOptimisticSelection(card, variantId, willSelect);
    setCardLoading(card, willSelect ? 'adding' : 'removing');
    setSubtotalShimmer(true);
    variantsInFlight.add(variantId);

    try {
      const data = await enqueueCartWrite(() =>
        postCartUpdate({ [variantId]: willSelect ? 1 : 0 })
      );

      closeModal(false);
      renderCartSectionsFromUpdate(data, variantId);

      // The card element was replaced by the re-render; re-derive truth from
      // the freshly rendered DOM against the live cart.
      await syncWithLiveCart();

      if (willSelect) {
        const newCard = activeRoot?.querySelector(getCardSelectorByVariantId(variantId));
        if (newCard && typeof newCard.focus === 'function') newCard.focus();
      }
    } catch (error) {
      // Roll back the optimistic flip, then reconcile with the live cart so
      // the card reflects the true server state even if the write partially
      // applied before failing (e.g. succeeded then a retry was rate limited).
      applyOptimisticSelection(card, variantId, wasSelected);
      setCardLoading(card, null);
      if (root) {
        setStatus(root, error.message || 'Could not update cart.', 'error');
      } else {
        console.error(error);
      }
      await syncWithLiveCart();
    } finally {
      variantsInFlight.delete(variantId);
      setSubtotalShimmer(false);
    }
  }

  // Back-compat wrapper (modal "Add to order" button still calls this).
  function addCartPageUpsell(variantId) {
    const card = getVisibleUpsellRoot()?.querySelector(getCardSelectorByVariantId(variantId));
    void toggleCartPageUpsell(card);
  }

  // --- Nav chevrons (horizontal rail scroll) -----------------------------
  // Presentation-only: scrolls the existing overflow-x rail. It never touches
  // cart state, card selection, or any data-* contract attribute.
  function getRailForNav(navBtn) {
    return navBtn?.closest(ROOT_SELECTOR)?.querySelector('.upsell-grid') || null;
  }

  function getRailScrollStep(rail) {
    const firstCard = rail.querySelector(CARD_SELECTOR);
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 160;
    // Advance ~2 cards per press, but never more than the visible width.
    return Math.min(cardWidth * 2 + 24, rail.clientWidth || cardWidth * 2);
  }

  function scrollUpsellRail(navBtn) {
    const rail = getRailForNav(navBtn);
    if (!rail) return;
    const direction = navBtn.dataset.upsellNav === 'prev' ? -1 : 1;
    rail.scrollBy({ left: direction * getRailScrollStep(rail), behavior: 'smooth' });
  }

  function updateNavState(rail) {
    if (!rail) return;
    const nav = rail.closest(ROOT_SELECTOR)?.querySelector('.upsell-nav');
    if (!nav) return;

    const maxScroll = rail.scrollWidth - rail.clientWidth;
    // No overflow → nothing to scroll; hide the whole control.
    nav.style.display = maxScroll <= 1 ? 'none' : '';

    const prevBtn = nav.querySelector('[data-upsell-nav="prev"]');
    const nextBtn = nav.querySelector('[data-upsell-nav="next"]');
    if (prevBtn) prevBtn.disabled = rail.scrollLeft <= 1;
    if (nextBtn) nextBtn.disabled = rail.scrollLeft >= maxScroll - 1;
  }

  function initNavForAllRoots() {
    document.querySelectorAll(`${ROOT_SELECTOR} .upsell-grid`).forEach((rail) => {
      updateNavState(rail);
      if (rail.dataset.navBound === 'true') return;
      rail.dataset.navBound = 'true';
      rail.addEventListener('scroll', () => updateNavState(rail), { passive: true });
    });
  }

  window.addEventListener('resize', () => {
    document.querySelectorAll(`${ROOT_SELECTOR} .upsell-grid`).forEach(updateNavState);
  });

  // P8 2026-08-10: re-hook ang nav state pagkatapos ng cart.js Section
  // Rendering refresh — pinapalitan nito ang .js-contents children (kasama
  // ang desktop upsell rail), kaya stale ang chevron disabled-states at wala
  // nang scroll listener ang bagong rail hangga't walang resize. Ang
  // 'cart:rerender' event ay pinutok ng FlowercartCartFooter module
  // (sections/main-cart-footer.liquid) — ang nag-iisang re-render observer ng
  // cart page. Sa PDP walang dispatcher, pero wala ring cart re-render doon.
  document.addEventListener('cart:rerender', (event) => {
    if (event.detail && event.detail.scope !== 'items') return;
    initNavForAllRoots();
  });

  document.addEventListener('click', (event) => {
    const navBtn = event.target.closest(NAV_SELECTOR);
    if (navBtn) {
      event.preventDefault();
      event.stopPropagation();
      scrollUpsellRail(navBtn);
      return;
    }

    const infoBtn = event.target.closest(INFO_SELECTOR);
    if (infoBtn) {
      event.preventDefault();
      event.stopPropagation();
      openModal(getCardFromSource(infoBtn), infoBtn);
      return;
    }

    const modalAddBtn = event.target.closest(MODAL_ADD_SELECTOR);
    if (modalAddBtn) {
      event.preventDefault();
      event.stopPropagation();

      if (isCartPage()) {
        addCartPageUpsell(modalAddBtn.dataset.variantId);
        return;
      }

      selectUpsellByVariantId(modalAddBtn.dataset.variantId);
      closeModal();
      return;
    }

    const closeBtn = event.target.closest('[data-modal-close]');
    if (closeBtn || event.target === modal) {
      event.preventDefault();
      closeModal();
      return;
    }

    const card = event.target.closest(CARD_SELECTOR);
    if (card) {
      event.preventDefault();

      if (isCartPage()) {
        highlightCartPageCard(card);
      } else {
        toggleUpsellCard(card);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && modal.classList.contains('is-active')) {
      closeModal();
      return;
    }

    if (event.key === 'Tab' && modal && modal.classList.contains('is-active')) {
      trapModalTab(event);
      return;
    }

    const card = event.target.closest?.(CARD_SELECTOR);
    if (!card) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (isCartPage()) {
        highlightCartPageCard(card);
      } else {
        toggleUpsellCard(card);
      }
    }
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const productForm = getProductFormFromEventTarget(event.target);
      if (!productForm) return;

      const submitBtn =
        productForm.querySelector('[type="submit"]') || productForm.querySelector('button[name="add"]');

      if (isSubmitting) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const root = activeRoot || getVisibleUpsellRoot();
      const defaultLabel = submitBtn ? submitBtn.textContent.trim() : 'Add to cart';

      try {
        isSubmitting = true;
        setButtonState(submitBtn, true, defaultLabel);
        if (root) setStatus(root, '', '');

        await addMainAndUpsells(productForm);

        setButtonState(submitBtn, false, defaultLabel);
      } catch (error) {
        setButtonState(submitBtn, false, defaultLabel);

        if (root) {
          setStatus(root, error.message || 'Could not add to cart.', 'error');
        } else {
          console.error(error);
          alert(error.message || 'Could not add to cart.');
        }
      } finally {
        isSubmitting = false;
      }
    },
    true
  );

  initModal();
  syncSelectedCards();
  initNavForAllRoots();

  // On the cart page, derive card selection from the live cart on load so a
  // reload (or a line-item removed in the cart table) keeps the upsell cards
  // in sync with what is actually in the cart.
  if (isCartPage()) {
    syncWithLiveCart();
  }
})();
